/**
 * 路由。结构对应用户心智的链路，不对应后端模块。
 *
 *   /season          工作台   这一季现在怎样、哪里卡住
 *   /season/layout   布置台   行怎么排、种什么
 *   /season/tasks    任务     田里谁做什么、进度怎样
 *   /season/confirm  确认队列 计划书里的上代行号到底指谁
 *   /season/decision 决策台   留谁、淘谁、进下一轮
 *   /base/*          基础管理
 */
import { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom'
import { ConfigProvider, Spin } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import './App.css'
import { antdTheme } from './theme'

import { AuthProvider, useAuth } from './context/AuthContext'
import { SeasonProvider } from './context/SeasonContext'
import AppLayout from './components/AppLayout'

import LoginPage from './pages/Login'
import EntryPage from './pages/Entry'
import ResetLoginPage from './pages/ResetLogin'
import ProfilePage from './pages/Profile'

const SeasonWorkspace = lazy(() => import('./pages/season/SeasonWorkspace'))
const LayoutBoard = lazy(() => import('./pages/season/LayoutBoard'))
const TaskBoard = lazy(() => import('./pages/season/TaskBoard'))
const VoiceNotes = lazy(() => import('./pages/season/VoiceNotes'))
const SeasonBatches = lazy(() => import('./pages/season/SeasonBatches'))
const SeedTestWorkbench = lazy(() => import('./pages/season/SeedTestWorkbench'))
const ExternalMaterialWorkbench = lazy(() => import('./pages/season/ExternalMaterialWorkbench'))
const ConfirmQueue = lazy(() => import('./pages/season/ConfirmQueue'))
const DecisionBoard = lazy(() => import('./pages/season/DecisionBoard'))
const PlanAllocationWorkbench = lazy(() => import('./pages/season/PlanAllocationWorkbench'))

const GermplasmLibrary = lazy(() => import('./pages/base/GermplasmLibrary'))
const FieldStations = lazy(() => import('./pages/base/FieldStations'))
const OrganizationPeople = lazy(() => import('./pages/base/OrganizationPeople'))

function PageFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
      <Spin />
    </div>
  )
}

function AuthedRoutes() {
  return (
    <SeasonProvider>
      <AppLayout>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/season" replace />} />
            <Route path="/season" element={<SeasonWorkspace />} />
            <Route path="/season/layout" element={<LayoutBoard />} />
            <Route path="/season/tasks" element={<TaskBoard />} />
            <Route path="/season/voice-notes" element={<VoiceNotes />} />
            <Route path="/season/batches" element={<SeasonBatches />} />
            <Route path="/season/seed-tests" element={<SeedTestWorkbench />} />
            <Route path="/season/external-materials" element={<ExternalMaterialWorkbench />} />
            <Route path="/season/confirm" element={<ConfirmQueue />} />
            <Route path="/season/decision" element={<DecisionBoard />} />
            <Route path="/season/allocations" element={<PlanAllocationWorkbench />} />
            <Route path="/base/germplasm" element={<GermplasmLibrary />} />
            <Route path="/base/field" element={<FieldStations />} />
            <Route path="/base/organization" element={<OrganizationPeople />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to="/season" replace />} />
          </Routes>
        </Suspense>
      </AppLayout>
    </SeasonProvider>
  )
}

function AppRoutes() {
  const { token, loading } = useAuth()

  if (loading) return <PageFallback />

  return (
    <Routes>
      <Route path="/entry" element={<EntryPage />} />
      <Route path="/login/:portal" element={<LoginPage />} />
      <Route path="/reset-login" element={<ResetLoginPage />} />
      <Route path="/*" element={token ? <AuthedRoutes /> : <Navigate to="/login/admin" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ConfigProvider locale={zhCN} theme={antdTheme}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ConfigProvider>
  )
}
